// src/components/InputToolBar.tsx

import React, { useCallback } from 'react';
import { X } from 'react-feather';
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import { FaRegHandPointer } from "react-icons/fa";
import { MdHearing } from "react-icons/md";
import { Button } from './button/Button';
import { Toggle } from './toggle/Toggle';
import { InputBar } from './InputBar';
import { MessageQueueIndicator } from './MessageQueueIndicator';
import { useContainerWidth } from '../hooks/useContainerWidth';
import { useFabricService } from '../contexts/FabricServiceContext';
import { useRealtimeService } from '../contexts/RealtimeServiceContext';

interface InputToolBarProps {

}

const WIDTH_THRESHOLD = 740;

export const InputToolBar: React.FC<InputToolBarProps> = ({

}) => {
  const { width, containerRef } = useContainerWidth();
  const showToggles = width === 0 || width > WIDTH_THRESHOLD;

  const {
    isFabric,
    setIsFabric,
    isFabricConnected,
    isFabricConnecting,
    connectFabric,
    disconnectFabric,
    pendingQueueCount,
    instance, model
  } = useFabricService();
  const {
    isConnected,
    isConnecting,
    canPushToTalk,
    connectConversation,
    disconnectConversation,
    changeTurnEndType
  } = useRealtimeService();

  const handleToggleConnection = useCallback(async () => {
    if (isConnected) {
      disconnectConversation();
      useWithFabric(disconnectFabric);
    } else {
      connectConversation();
      useWithFabric(connectFabric);
    }
  }, [isFabric, isConnected, isFabricConnected]);

  const useWithFabric = useCallback((func: () => Promise<void>) => {
    if (isFabric) {
      return func();
    }
  }, [isFabric]);

  const onlyIfFabric = useCallback((value: boolean, defaultValue: boolean = true) => {
    return isFabric ? value : defaultValue;
  }, [isFabric]);

  const getConnectionButtonProps = () => {
    if (isConnected && onlyIfFabric(isFabricConnected)) {
      return {
        icon: X,
        buttonStyle: 'regular' as const,
        iconPosition: 'end' as const,
        iconSize: 22,
        iconColor: "grey" as const,
      };
    }
    if (isConnecting || onlyIfFabric(isFabricConnecting, false)) {
      return {
        label: '',
        buttonStyle: 'action' as const,
        isLoading: true,
        iconSize: 22,
      };
    }
    return {
      label: '',
      icon: HiOutlineChatBubbleLeftRight,
      buttonStyle: 'action' as const,
      iconPosition: 'start' as const,
      iconSize: 22,
    };
  };

  return (
    <div className="content-actions" ref={containerRef}>
      <div style={{ position: 'relative', width: '100%' }}>
        <MessageQueueIndicator count={pendingQueueCount} />
        <InputBar />
      </div>
      <div className={`toggle-container ${(showToggles && !isFabric) ? 'visible' : 'hidden'}`}>
        <Toggle
          defaultValue={canPushToTalk ? 'none' : 'server_vad'} // Temp default fabric
          labels={['Push to Talk', 'Listening']}
          icons={[<FaRegHandPointer size={18} />, <MdHearing size={18} />]}
          values={['none', 'server_vad']}
          onChange={(_, value) => {
            const currentValue = canPushToTalk ? 'none' : 'server_vad'
            if (currentValue === value) return
            changeTurnEndType(value)
          }
          }
        />
      </div>
      <Button
        {...getConnectionButtonProps()}
        onClick={handleToggleConnection}
        disabled={
          isConnecting || 
          onlyIfFabric(isFabricConnecting, false) || 
          onlyIfFabric(instance === undefined, false) || 
          onlyIfFabric(model === '', false)
        }
      />
    </div>
  );
};